import { Injectable, inject } from '@angular/core';
import { EcaSetupConfigurationService, SetupConfigAttribute } from '@drc-eca/eca-components-lib';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OktaTenantService {
  private setupConfigService = inject(EcaSetupConfigurationService);
  private readonly opaProductCodes = new Set(['DEOCS', 'OPA']);

  private get defaultSamlRedirectUrl(): string {
    // TODO: This env fallback is still needed for non-OPA clients and no-client
    // flows until every environment/client is migrated to setup-config-driven
    // okta-saml-redirect-url resolution.
    return environment.okta.samlRedirectUrl;
  }

  private isOpaProduct(productCode?: string): boolean {
    return !!productCode && this.opaProductCodes.has(productCode.toUpperCase());
  }

  private getOverrideRedirectUrl(attributes: SetupConfigAttribute[]): string | undefined {
    return attributes.find((attr) => attr.attribute?.key === 'okta-saml-redirect-url')?.value;
  }

  getSamlRedirectUrl(productCode?: string): Observable<string> {
    if (!productCode) {
      return of(this.defaultSamlRedirectUrl);
    }

    return this.setupConfigService.getAttributesForClient(productCode).pipe(
      map((attributes: SetupConfigAttribute[]) => {
        const override = this.getOverrideRedirectUrl(attributes);
        if (override) {
          return override;
        }

        if (this.isOpaProduct(productCode)) {
          throw new Error(`Missing required setup-config attribute okta-saml-redirect-url for product ${productCode}`);
        }

        return this.defaultSamlRedirectUrl;
      }),
      catchError((error) => this.isOpaProduct(productCode)
        ? throwError(() => error)
        : of(this.defaultSamlRedirectUrl)
      )
    );
  }

  getTenantOrigin(productCode?: string): Observable<string> {
    return this.getSamlRedirectUrl(productCode).pipe(
      map((samlRedirectUrl) => new URL(samlRedirectUrl).origin),
      catchError((error) => this.isOpaProduct(productCode)
        ? throwError(() => error)
        : of(new URL(this.defaultSamlRedirectUrl).origin)
      )
    );
  }
}